import React from 'react';
import SystemActions from '@shared/actions/SystemActions';
import { DialogComponent, TooltipComponent } from '@syncfusion/ej2-react-popups';
import UrlUtilities from '@shared/common/UrlUtilities';
import FacilityMap from '../FacilityMap';
import FacilityStore from '../../stores/FacilityStore';
import ComponentHelpers from '../../helpers/ComponentHelpers';
import KnowledgeBookActions from '../../actions/KnowledgeBookActions';
import ClassificationTags from './ClassificationTags';
import KnowledgeLibraryOntologyFilter from './KnowledgeLibraryOntologyFilter';
import {
    getOntologyScopedScanPositions,
    isNewKnowledgeBookCreationInputValid,
    isPanoFirstViewAvailable,
    KnowledgeBookFirstView,
    NewKnowledgeBookCreationInput,
    NewKnowledgeBookCreationResult,
} from './knowledgeBookCreationModel';

interface NewKnowledgeBookDialogProps {
    initialClassificationTagId?: string;
    facility: IFacilityDetail;
    onClosed: () => void;
    onCreated: (result: NewKnowledgeBookCreationResult) => Promise<void> | void;
}

const NewKnowledgeBookDialog: React.FC<NewKnowledgeBookDialogProps> = ({ facility, onClosed, onCreated, initialClassificationTagId }) => {
    const rootOntology = FacilityStore.getRootOntologyPart() ?? facility?.OntologyParts?.find(part => !part.OntologyParentID);
    const [ontologyId, setOntologyId] = React.useState(rootOntology?.ID ?? '');
    const [name, setName] = React.useState('');
    const [description, setDescription] = React.useState('');
    const [classificationTags, setClassificationTags] = React.useState<string[]>(() => {
        const tags = FacilityStore.getVisibleTaxonomyClassificationDetail()?.ClassificationTags ?? [];
        const tag = tags.find(item => item.ID === initialClassificationTagId);
        return tag && ComponentHelpers.getClassificationTagPermissionLevel(tag, tags) >= 2 ? [tag.ID] : [];
    });
    const initialClassificationTags = React.useRef<string[] | null>(initialClassificationTagId ? [...classificationTags] : null);
    const handleClassificationTagsChange = React.useCallback((tags: string[]) => {
        if (initialClassificationTags.current === null) initialClassificationTags.current = [...tags];
        setClassificationTags(tags);
    }, []);
    const [firstView, setFirstView] = React.useState<KnowledgeBookFirstView>('3d');
    const [scanPositionId, setScanPositionId] = React.useState<string | undefined>();
    const [currentPage, setCurrentPage] = React.useState<1 | 2>(1);
    const [showValidationErrors, setShowValidationErrors] = React.useState(false);
    const [showPanoValidation, setShowPanoValidation] = React.useState(false);
    const [isSaving, setIsSaving] = React.useState(false);
    const [resizeCount, setResizeCount] = React.useState(0);

    const facilityHierarchy = React.useMemo(() => FacilityStore.getFacilityHierarchy(), [facility]);
    const selectedOntology = React.useMemo(
        () => facility?.OntologyParts?.find(part => part.ID === ontologyId) ?? null,
        [facility?.OntologyParts, ontologyId]
    );
    const scanPositions = React.useMemo(
        () => getOntologyScopedScanPositions(facility, ontologyId),
        [facility, ontologyId]
    );
    const panoAvailable = isPanoFirstViewAvailable(selectedOntology, scanPositions);
    const selectedScanPosition = scanPositions.find(view => view.ID === scanPositionId);
    const input: NewKnowledgeBookCreationInput = {
        name,
        description,
        classificationTags,
        ontologyId,
        firstView,
        scanPositionId,
    };
    const isDetailsValid = !!name.trim() && !!ontologyId && classificationTags.length > 0;

    React.useEffect(() => {
        if (firstView === 'pano' && !panoAvailable) {
            setFirstView('3d');
        }
    }, [firstView, panoAvailable]);

    React.useEffect(() => {
        setScanPositionId(undefined);
        setResizeCount(value => value + 1);
    }, [ontologyId]);

    React.useEffect(() => {
        if (firstView === 'pano') {
            const timer = window.setTimeout(() => {
                setResizeCount(value => value + 1);
                window.dispatchEvent(new Event('resize'));
            }, 0);
            return () => window.clearTimeout(timer);
        }
    }, [firstView, currentPage]);

    const handleOntologyChange = React.useCallback((value?: string) => {
        if (value === ontologyId) return;

        setScanPositionId(undefined);
        setOntologyId(value ?? '');
    }, [ontologyId]);

    const handleNext = () => {
        setShowValidationErrors(true);
        if (isDetailsValid) {
            setCurrentPage(2);
            setShowPanoValidation(false);
        }
    };

    const handleBack = () => {
        setCurrentPage(1);
        setShowPanoValidation(false);
    };

    const handleCreate = React.useCallback(async () => {
        setShowPanoValidation(true);
        if (!isNewKnowledgeBookCreationInputValid(input, panoAvailable, !!selectedScanPosition) || isSaving) {
            return;
        }

        setIsSaving(true);
        try {
            const result = await KnowledgeBookActions.createKnowledgeBookFromLibrary(input);
            await onCreated(result);
            onClosed();
        } finally {
            setIsSaving(false);
        }
    }, [input, isSaving, onClosed, onCreated, panoAvailable, selectedScanPosition]);

    const handleClose = () => {
        if (isSaving) return;
        const tagsChanged = initialClassificationTags.current !== null && (
            classificationTags.length !== initialClassificationTags.current.length ||
            classificationTags.some(tag => !initialClassificationTags.current!.includes(tag))
        );
        if (name || description || tagsChanged || ontologyId !== (rootOntology?.ID ?? '') || firstView !== '3d') {
            SystemActions.PublicActions.displaySaveChangesDialog({
                message: InovxLoader.t('common:msgBox.saveChanges'),
                confirmCallback: async () => {
                    setShowValidationErrors(true);
                    if (!isDetailsValid) {
                        setCurrentPage(1);
                        return;
                    }
                    setCurrentPage(2);
                    await handleCreate();
                },
                cancelCallback: onClosed,
            });
        } else {
            onClosed();
        }
    };

    const handleFirstViewChange = (value: KnowledgeBookFirstView) => {
        if (value === 'pano' && !panoAvailable) return;
        if (value === '3d') {
            setScanPositionId(undefined);
            setShowPanoValidation(false);
        }
        setFirstView(value);
    };

    const renderFooter = () => (
        <div className="new-knowledge-book-dialog-footer">
            {currentPage === 1 ? (
                <>
                    <button type="button" className="btn btn-default" disabled={isSaving} onClick={handleClose}>
                        {InovxLoader.t('facility:preferences.cancelButton')}
                    </button>
                    <button type="button" className="btn btn-primary" disabled={isSaving} onClick={handleNext}>
                        {InovxLoader.t('facility:dataSources.next')}
                    </button>
                </>
            ) : (
                <>
                    <button type="button" className="btn btn-default new-knowledge-book-back-button" disabled={isSaving} onClick={handleBack}>
                        {InovxLoader.t('facility:dataSources.back')}
                    </button>
                    <button type="button" className="btn btn-default" disabled={isSaving} onClick={handleClose}>
                        {InovxLoader.t('facility:preferences.cancelButton')}
                    </button>
                    <button type="button" className="btn btn-primary" disabled={isSaving} onClick={() => void handleCreate()}>
                        {InovxLoader.t('facility:action.create')}
                    </button>
                </>
            )}
        </div>
    );

    return (
        <DialogComponent
            target="#app"
            cssClass="new-knowledge-book-dialog"
            width="min(920px, 92vw)"
            height={`min(${currentPage === 2 && firstView === 'pano' ? 800 : 600}px, 92vh)`}
            header={InovxLoader.t('facility:knowledgeBook.newKnowledgeBook')}
            footerTemplate={renderFooter}
            showCloseIcon={true}
            visible={true}
            beforeClose={(args: { cancel?: boolean }) => {
                args.cancel = true;
                handleClose();
            }}
            isModal={true}
            zIndex={110000}
        >
            <div className="new-knowledge-book-dialog-content">
                <div className="new-knowledge-book-stepper" aria-label={InovxLoader.t('facility:knowledgeBook.newKnowledgeBook')}>
                    <div className={`new-knowledge-book-step ${currentPage === 1 ? 'active' : 'complete'}`}>
                        <span className="new-knowledge-book-step-number">{currentPage === 1 ? '1' : '✓'}</span>
                        <span>{InovxLoader.t('facility:knowledgeBook.newKnowledgeBookStepBookDetails')}</span>
                    </div>
                    <span className="new-knowledge-book-step-line" aria-hidden="true" />
                    <div className={`new-knowledge-book-step ${currentPage === 2 ? 'active' : ''}`}>
                        <span className="new-knowledge-book-step-number">2</span>
                        <span>{InovxLoader.t('facility:knowledgeBook.newKnowledgeBookStepStartingView')}</span>
                    </div>
                </div>

                {currentPage === 1 ? (
                    <div className="new-knowledge-book-page new-knowledge-book-details-page">
                        <div className="new-knowledge-book-dialog-fields">
                            <div className={`new-knowledge-book-field ${showValidationErrors && !ontologyId ? 'has-error' : ''}`}>
                                <label htmlFor="new-knowledge-book-ontology">
                                    {InovxLoader.t('facility:knowledgeBook.ontology')} <span className="new-knowledge-book-required" aria-hidden="true">*</span>
                                </label>
                                <div className="d-flex w-100">
                                    <div id="new-knowledge-book-ontology" className="w-100">
                                        <KnowledgeLibraryOntologyFilter
                                            facilityHierarchy={facilityHierarchy}
                                            selectedOntologyId={ontologyId}
                                            showClearButton={false}
                                            onChange={handleOntologyChange}
                                        />
                                    </div>
                                    <div className="add-on-icon">
                                        <TooltipComponent content={InovxLoader.t('facility:knowledgeBook.ontologyLimitKnowledgeViews').replace('__ontology__', selectedOntology?.Name || '')} opensOn='Hover' target='#hoverOntologyInfo' position="LeftBottom">
                                            <i id='hoverOntologyInfo' className="info-icon"></i>
                                        </TooltipComponent>
                                    </div>
                                </div>
                                {showValidationErrors && !ontologyId && <div className="new-knowledge-book-error">{InovxLoader.t('facility:knowledgeBook.ontologyRequired')}</div>}
                            </div>

                            <div className={`new-knowledge-book-field mt-1 ${showValidationErrors && !name.trim() ? 'has-error' : ''}`}>
                                <label htmlFor="new-knowledge-book-name">
                                    {InovxLoader.t('facility:knowledgeBook.title')} <span className="new-knowledge-book-required" aria-hidden="true">*</span>
                                </label>
                                <input
                                    id="new-knowledge-book-name"
                                    className="form-control"
                                    value={name}
                                    placeholder={InovxLoader.t('facility:form.knowledgeBookNamePlaceholder')}
                                    autoComplete="off"
                                    aria-required="true"
                                    onChange={event => setName(event.target.value)}
                                />
                                {showValidationErrors && !name.trim() && <div className="new-knowledge-book-error">{InovxLoader.t('facility:knowledgeBook.nameRequired')}</div>}
                            </div>

                            <div className="new-knowledge-book-field mt-1 new-knowledge-book-description-field">
                                <label htmlFor="new-knowledge-book-description">
                                    {InovxLoader.t('facility:knowledgeBook.knowledgeBookDescription')} <span className="new-knowledge-book-optional">({InovxLoader.t('facility:knowledgeBook.newKnowledgeBookOptional')})</span>
                                </label>
                                <textarea
                                    id="new-knowledge-book-description"
                                    className="form-control"
                                    value={description}
                                    placeholder={InovxLoader.t('facility:form.knowledgeBookDescriptionPlaceholder')}
                                    onChange={event => setDescription(event.target.value)}
                                />
                            </div>
                        </div>

                        <div className={`new-knowledge-book-field new-knowledge-book-tags taxonomy-wrapper ${showValidationErrors && classificationTags.length === 0 ? 'has-error' : ''}`}>
                            <label>
                                {InovxLoader.t('facility:knowledgeBook.classificationTags')} <span className="new-knowledge-book-required" aria-hidden="true">*</span>
                            </label>
                            <ClassificationTags
                                selectedTags={classificationTags}
                                onSelectedTagsChange={handleClassificationTagsChange}
                                restrictToCurrentUserPermissions={true}
                                defaultSelectEnabledTags={!initialClassificationTagId}
                                showSelectAll={true}
                                height={300}
                            />
                        </div>
                        <div className="new-knowledge-book-tags-error new-knowledge-book-error">
                            {showValidationErrors && classificationTags.length === 0 && InovxLoader.t('facility:knowledgeBook.classificationTagsRequired')}
                        </div>
                    </div>
                ) : (
                    <div className="new-knowledge-book-page new-knowledge-book-starting-view-page">
                        <div className="new-knowledge-book-section-title">{InovxLoader.t('facility:knowledgeBook.newKnowledgeBookHowShouldOpen')}</div>
                        <div className="new-knowledge-book-view-cards" role="radiogroup" aria-label={InovxLoader.t('facility:knowledgeBook.firstView')}>
                            <label className={`new-knowledge-book-view-card ${firstView === '3d' ? 'selected' : ''}`}>
                                <img alt="" aria-hidden="true" src={`${UrlUtilities.getBaseApplicationUrl()}/images/menus/3d_model.png`} />
                                <span className="new-knowledge-book-view-card-copy">
                                    <strong>
                                        <input
                                            type="radio"
                                            name="new-knowledge-book-first-view"
                                            checked={firstView === '3d'}
                                            onChange={() => handleFirstViewChange('3d')}
                                        />
                                        {InovxLoader.t('facility:menu.facilityModel')}
                                    </strong>
                                    <span>{InovxLoader.t('facility:knowledgeBook.newKnowledgeBook3DDescription')}</span>
                                </span>
                            </label>
                            <label className={`new-knowledge-book-view-card ${firstView === 'pano' ? 'selected' : ''} ${!panoAvailable ? 'disabled' : ''}`} title={!panoAvailable ? InovxLoader.t('facility:knowledgeBook.panoUnavailable') : undefined}>
                                <img alt="" aria-hidden="true" src={`${UrlUtilities.getBaseApplicationUrl()}/images/menus/360Image.png`} />
                                <span className="new-knowledge-book-view-card-copy">
                                    <strong>
                                        <input
                                            type="radio"
                                            name="new-knowledge-book-first-view"
                                            checked={firstView === 'pano'}
                                            disabled={!panoAvailable}
                                            onChange={() => handleFirstViewChange('pano')}
                                        />
                                        {InovxLoader.t('facility:menu.360ImageView')}
                                    </strong>
                                    <span>{InovxLoader.t('facility:knowledgeBook.newKnowledgeBookPanoDescription')}</span>
                                </span>
                            </label>
                        </div>
                        {!panoAvailable && <div className="new-knowledge-book-help">{InovxLoader.t('facility:knowledgeBook.panoUnavailable')}</div>}

                        {firstView === 'pano' && <div className="new-knowledge-book-map-section new-knowledge-book-scan-position-field">
                            <div className="new-knowledge-book-section-title">
                                <span>{InovxLoader.t('facility:knowledgeBook.selectScanPosition')} <span className="new-knowledge-book-required" aria-hidden="true">*</span></span>
                                {selectedScanPosition && <span className="new-knowledge-book-selected-scan">{InovxLoader.t('facility:knowledgeBook.selected')}: {selectedScanPosition.Name}</span>}
                            </div>
                            <div className="new-knowledge-book-map" data-testid="new-knowledge-book-map">
                                {selectedOntology?.Map?.MapFileImageUrl ? (
                                    <FacilityMap
                                        siteMap={selectedOntology.Map}
                                        views={scanPositions}
                                        activeView={selectedScanPosition}
                                        activeMarkerAsCircle={true}
                                        showElements={true}
                                        isMiniMap={false}
                                        resizeCount={resizeCount}
                                        onViewMarkerClicked={view => {
                                            setScanPositionId(view.ID);
                                            setShowPanoValidation(false);
                                        }}
                                    />
                                ) : (
                                    <div className="new-knowledge-book-map-empty">{InovxLoader.t('facility:knowledgeBook.panoUnavailable')}</div>
                                )}
                            </div>
                            {showPanoValidation && !selectedScanPosition && <div className="new-knowledge-book-error">{InovxLoader.t('facility:knowledgeBook.scanPositionRequired')}</div>}
                        </div>}
                    </div>
                )}
            </div>
        </DialogComponent>
    );
};

export default NewKnowledgeBookDialog;
